import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gameApi } from "@/lib/api";
import { HorseSilhouette } from "@/components/game/HorseSilhouette";
import { ArrowLeft, Medal, Loader2, Trophy } from "lucide-react";
import { DisciplineLeaderboard } from "@/components/game/HorseCareer";

const PERIODS = [{ k: "all", l: "All Time" }, { k: "month", l: "This Month" }, { k: "season", l: "This Season" }];
const DISCIPLINES = ["Barrel Racing", "Cutting", "Dressage", "Reining", "Jumpers", "Eventing", "Driving"];

export const Leaderboard = ({ onBack, onOpenProfile }) => {
  const navigate = useNavigate();
  const [cat, setCat] = useState("overall");
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("players");
  const [disc, setDisc] = useState("Barrel Racing");
  useEffect(() => {
    setLoading(true);
    gameApi.get(`/game/leaderboard?category=${cat}&period=${period}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [cat, period]);
  const cats = data?.categories || [];
  return (
    <div className="pb-10" data-testid="leaderboard-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="lb-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <Medal className="text-amber-500" size={20} /><h2 className="font-display text-xl font-bold text-primary">Leaderboard</h2>
      </div>
      <div className="px-4 pt-3">
        <div className="mb-3 flex gap-1 rounded-xl bg-muted p-1" data-testid="lb-mode-toggle">
          <button data-testid="lb-mode-players" onClick={() => setMode("players")}
            className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${mode === "players" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Players</button>
          <button data-testid="lb-mode-disciplines" onClick={() => setMode("disciplines")}
            className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${mode === "disciplines" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Disciplines</button>
          <button data-testid="lb-mode-barns" onClick={() => setMode("barns")}
            className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${mode === "barns" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Barns</button>
        </div>
        {mode === "barns" ? (<BarnLbBoard />) : mode === "disciplines" ? (
          <div data-testid="discipline-lb-section">
            <select data-testid="lb-discipline" value={disc} onChange={(e) => setDisc(e.target.value)}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-primary">
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <p className="mt-2 mb-2 flex items-center gap-1 text-xs text-muted-foreground"><Trophy size={12} className="text-amber-500" /> Top horses in {disc}, ranked by career points.</p>
            <DisciplineLeaderboard discipline={disc} />
          </div>
        ) : (<>
        <select data-testid="lb-category" value={cat} onChange={(e) => setCat(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-primary">
          {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <div className="mt-2 flex gap-1 rounded-xl bg-muted p-1">
          {PERIODS.map((p) => (
            <button key={p.k} data-testid={`lb-period-${p.k}`} onClick={() => setPeriod(p.k)}
              className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${period === p.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{p.l}</button>
          ))}
        </div>
        {loading ? (
          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p>
        ) : (data?.entries?.length ? (
          <div className="mt-3 space-y-2">
            {data.entries.map((e) => (
              <button key={e.player_number} data-testid={`lb-row-${e.rank}`} onClick={() => onOpenProfile(e.player_number)}
                className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left active:scale-[0.99]">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-extrabold ${e.rank <= 3 ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground"}`}>{e.rank}</span>
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg" style={{ background: `${e.thumbnail_color || "#cccccc"}1a` }}><HorseSilhouette color={e.thumbnail_color || "#8a8a8a"} size={36} /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-primary"><span style={{ color: e.accent_color || undefined }}>{e.display_name}</span> <span className="text-xs font-normal text-muted-foreground">#{e.player_number}</span>{e.barn_tag && <span data-testid={`lb-barn-${e.rank}`} role="button" onClick={(ev) => { ev.stopPropagation(); navigate(`/game/barn/${e.barn_id}`); }} className="ml-1.5 rounded-full bg-secondary/15 px-1.5 py-0.5 text-[9px] font-black text-secondary">[{e.barn_tag}]</span>}{typeof e.level === "number" && <span data-testid={`lb-level-${e.rank}`} className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">Lv {e.level}</span>}</p>
                  <p className="truncate text-xs text-secondary">{e.stable_name}{e.favorite_horse ? ` · ${e.favorite_horse}` : ""}</p>
                </div>
                <span data-testid={`lb-value-${e.rank}`} className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-sm font-extrabold text-primary">{e.value}</span>
              </button>
            ))}
          </div>
        ) : <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="lb-empty">No ranked players yet. Complete a makeover to get on the board!</p>)}
        </>)}
      </div>
    </div>
  );
};

const BarnLbBoard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("overall");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); gameApi.get(`/game/barns/leaderboard/board?period=${period}`).then((r) => setD(r.data)).catch(() => setD({ rows: [], my_barn: null })); }, [period]);
  const myId = d?.my_barn?.id;
  const PB = ({ id, label }) => (
    <button data-testid={`barn-lb-period-${id}`} onClick={() => setPeriod(id)} className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${period === id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{label}</button>
  );
  return (
    <div className="mt-3" data-testid="barn-lb-section">
      <div className="mb-2 flex gap-1 rounded-xl bg-muted p-1" data-testid="barn-lb-period-toggle"><PB id="overall" label="Overall" /><PB id="monthly" label="This Month" /><PB id="weekly" label="This Week" /></div>
      {!d ? <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p> : (<>
      <p className="mb-2 flex items-center gap-1 text-xs text-muted-foreground"><Trophy size={12} className="text-amber-500" /> Training Barns ranked by Barn Points from members' results, chores &amp; goals.</p>
      <p className="mb-2 flex items-center gap-1 text-xs text-muted-foreground"><Trophy size={12} className="text-amber-500" /> Training Barns ranked by total Barn Points earned from members' results, chores &amp; goals.</p>
      {d.rows.length === 0 ? <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="barn-lb-empty">No barns ranked yet.</p> : (
        <div className="space-y-2" data-testid="barn-lb-rows">
          {d.rows.map((r) => (
            <button key={r.id} data-testid={`barn-lb-row-${r.rank}`} onClick={() => navigate(`/game/barn/${r.id}`)}
              className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left active:scale-[0.99] ${r.id === myId ? "border-primary bg-primary/10" : "border-border bg-card"}`}>
              <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-extrabold ${r.rank <= 3 ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground"}`}>{r.rank}</span>
              <span className="h-9 w-9 shrink-0 rounded-lg" style={{ background: `linear-gradient(135deg, ${r.color_primary || "#3a5a40"}, ${r.color_secondary || "#a3b18a"})` }} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-primary">{r.name} <span className="text-xs font-black text-secondary">[{r.tag}]</span>{r.id === myId && <span className="ml-1 text-[10px] font-bold text-primary">· Your Barn</span>}</p>
                <p className="truncate text-xs text-muted-foreground">{r.member_count}👥 · {r.first_place_wins || 0} 🥇 · {r.total_placings || 0} placings</p>
              </div>
              <span data-testid={`barn-lb-points-${r.rank}`} className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-sm font-extrabold text-primary">{r.points}</span>
            </button>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
};
